import { useCallback, useRef, useState } from 'react';
import type {
  EProtectErrorResponse,
  EProtectFormFields,
  EProtectRequest,
  EProtectSuccessResponse,
} from '../../../types/runtime';

export interface UseEProtectOptions {
  customerServicePhone: string;
}

export interface UseEProtectResult {
  loading: boolean;
  error: string | null;
  sendToEprotect: (cardInputRef: HTMLInputElement | null) => void;
  reset: () => void;
}

function getEProtectConfig() {
  const config = window.mxAppConfig?.eProtect;
  return {
    url: config?.NX_EPROTECT_URL || '',
    paypageId: config?.NX_PAYPAGE_ID || '',
  };
}

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const scripts = document.querySelectorAll('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src === url || scripts[i].getAttribute('src') === url) {
        resolve();
        return;
      }
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load eProtect SDK.'));
    document.head.appendChild(script);
  });
}

export function useEProtect(
  onSuccess: (response: EProtectSuccessResponse) => void,
  onError: (message: string) => void,
  options: UseEProtectOptions
): UseEProtectResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptRef = useRef(0);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  const sendToEprotect = useCallback(
    (cardInputRef: HTMLInputElement | null) => {
      const config = getEProtectConfig();

      if (!config.url || !config.paypageId) {
        const msg = `eProtect is not configured. Please contact customer service at ${options.customerServicePhone}.`;
        setError(msg);
        onError(msg);
        return;
      }

      setLoading(true);
      setError(null);
      attemptRef.current += 1;
      const currentAttempt = attemptRef.current;

      // orderId/id are pinned to fixed values, not generated per-submit.
      // A crypto.randomUUID() value was tried here (Sprint 3 Phase A, for PCI
      // hygiene) and broke the eProtect request; the legacy AngularJS
      // implementation shows the same abandoned attempt (see
      // billingInformationController.js / changeCreditCardController.js).
      // Vantiv/eProtect is known to reject out-of-spec field values elsewhere
      // in this integration (reportGroup throws error 889 if too long), so a
      // 36-char hyphenated UUID for orderId/id is the suspected cause. Do not
      // reintroduce dynamic values here without first confirming acceptance
      // against the DEV/QA eProtect sandbox (vantivprelive.com).
      const orderId = 'order123';
      const id = '123456';

      const request: EProtectRequest = {
        paypageId: config.paypageId,
        reportGroup: 'MPBI',
        orderId,
        id,
        url: config.url,
        minPanLength: 15,
      };

      const formFields: EProtectFormFields = {
        accountNum: cardInputRef,
      };

      const handleSuccess = (response: EProtectSuccessResponse) => {
        if (currentAttempt !== attemptRef.current) return;
        setLoading(false);
        setError(null);
        onSuccess({ ...response, orderId });
      };

      const handleError = (response: EProtectErrorResponse) => {
        if (currentAttempt !== attemptRef.current) return;
        setLoading(false);
        const msg =
          'There was a problem processing your card. Please confirm your information and try again.' +
          ` If the issue persists, please contact customer service at ${options.customerServicePhone}.`;
        setError(msg);
        onError(msg);
      };

      const handleTimeout = () => {
        if (currentAttempt !== attemptRef.current) return;
        setLoading(false);
        const msg =
          'The payment processing request timed out. Please try again.' +
          ` If the issue persists, please contact customer service at ${options.customerServicePhone}.`;
        setError(msg);
        onError(msg);
      };

      loadScript(config.url)
        .then(() => {
          if (currentAttempt !== attemptRef.current) return;

          if (typeof window.eProtect !== 'function') {
            throw new Error('eProtect SDK loaded but class is not available.');
          }

          const eprotect = new window.eProtect();
          eprotect.sendToEprotect(
            request,
            formFields,
            handleSuccess,
            handleError,
            handleTimeout,
            15000
          );
        })
        .catch(() => {
          if (currentAttempt !== attemptRef.current) return;
          setLoading(false);
          const msg =
            'Unable to load the payment processing service. Please try again.' +
            ` If the issue persists, please contact customer service at ${options.customerServicePhone}.`;
          setError(msg);
          onError(msg);
        });
    },
    [onSuccess, onError, options.customerServicePhone]
  );

  return { loading, error, sendToEprotect, reset };
}
